// frontend/src/components/AddWidgetModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-17
// Updated:      2026-09-23 14:33 BST
//
// Description:
//   Shared widget picker for configurable pages. Displays only widgets explicitly
//   supplied by the page or registered for the requested presentation surface.
//
// Change Summary (2026-09-23):
//   - Constrained the modal to the usable application viewport below the fixed top navigation.
//   - Added a 64px fallback top-navigation offset via `--app-top-nav-height`.
//   - Capped the picker height so roughly eight normal widget entries are visible at once.
//   - Widget results now scroll internally while the title and close control remain fixed.
//   - Retained dynamic viewport units so mobile browser chrome / keyboard changes remain safe.
//
// Change Summary (2026-09-10):
//   - Retained the existing responsive modal architecture; no redesign was required.
//   - Switched mobile height constraints to dynamic viewport units (`100dvh`) so the
//     picker remains usable when browser chrome or the software keyboard changes height.
//   - Hardened narrow-screen width handling and overscroll containment.
//   - Increased the mobile close target to a full 44px touch target.
//   - Retained minimum 48px widget-row targets and added touch scrolling hints.
//
// Change Summary (2026-09-07):
//   - Fixed canonical widget-key resolution for raw API/database widget rows.
//   - Raw backend rows use numeric `id` plus string `name`; `name` is now the
//     canonical selection/exclusion key when present.
//   - Retains compatibility with legacy frontend registry objects whose `id`
//     is already the string widget key.
//   - Fixed touch/mobile close-button visibility.
//   - Improved mobile modal width, height, scrolling, and touch target sizing.
//
// Change Summary (2026-07-30):
//   - Added `surface` and `widgets` inputs and stopped relying exclusively on the global catalogue.
//   - Uses WidgetSurface for canonical, non-mutating surface filtering.
//   - Retains category filtering and a registry fallback for legacy pages during migration.
//   - Guards invalid selections and preserves the existing theme and modal behaviour.
//
// Operational Notes:
//   - Migrated pages must pass `surface`; direct global-registry fallback is transitional only.
//   - `widgets` may be supplied for permission/licence-filtered catalogues and takes precedence.
//   - Surface membership is a presentation allow-list, not an authorisation decision.
//   - Backend widget rows commonly use numeric `id` and string `name`; never treat the
//     numeric database primary key as the frontend widget registry key.
//   - If the application top-nav height changes, set CSS variable `--app-top-nav-height`
//     at the application shell; this component falls back safely to 64px.

import React, { useMemo } from "react";
import WidgetRegistry from "../widgets/WidgetRegistry";
import { getSurfaceWidgets } from "../widgets/WidgetSurface";
import { useTheme } from "../context/ThemeContext.jsx";

const getWidgetKey = (widget) => {
  if (!widget || typeof widget !== "object") return "";

  if (typeof widget.name === "string" && widget.name.trim()) {
    return widget.name.trim();
  }

  if (typeof widget.id === "string" && widget.id.trim()) {
    return widget.id.trim();
  }

  return "";
};

const AddWidgetModal = ({
  onClose,
  onSelect,
  surface,
  widgets,
  categoryFilter,
  excludedWidgetIds = [],
}) => {
  const { theme } = useTheme();

  const filteredWidgets = useMemo(() => {
    const source = Array.isArray(widgets)
      ? widgets
      : surface
        ? getSurfaceWidgets(surface)
        : WidgetRegistry.filter((widget) => widget.is_active !== false);

    const excluded = new Set(
      (Array.isArray(excludedWidgetIds) ? excludedWidgetIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );

    return source.filter((widget) => {
      const widgetKey = getWidgetKey(widget);

      if (!widgetKey) return false;
      if (widget.is_active === false) return false;
      if (categoryFilter && widget.category !== categoryFilter) return false;

      return !excluded.has(widgetKey);
    });
  }, [categoryFilter, excludedWidgetIds, surface, widgets]);

  const listItemHoverStyle = {
    backgroundColor: theme.accent_color ? `${theme.accent_color}1A` : "#f7f7f7",
  };

  const selectWidget = (widget) => {
    const widgetKey = getWidgetKey(widget);
    if (!widgetKey) return;
    onSelect(widgetKey);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center overflow-hidden bg-black bg-opacity-50 p-2 sm:p-3"
      onClick={onClose}
      role="presentation"
      style={{
        top: "var(--app-top-nav-height, 64px)",
      }}
    >
      <div
        className="group relative flex w-[calc(100vw-16px)] max-w-md flex-col overflow-hidden rounded bg-white p-4 shadow-lg sm:w-full md:p-6"
        onClick={(event) => event.stopPropagation()}
        style={{
          borderRadius: "12px",
          fontFamily: "var(--font-body)",
          maxHeight:
            "min(720px, calc(100dvh - var(--app-top-nav-height, 64px) - 16px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-widget-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Add Widget Modal"
          title="Close"
          className="absolute right-2 top-2 z-10 flex h-11 w-11 touch-manipulation items-center justify-center text-lg font-bold opacity-100 transition-opacity duration-200 md:h-7 md:w-7 md:text-sm md:opacity-0 md:group-hover:opacity-100"
          style={{
            backgroundColor: theme.button_action?.normal || "#ff6600",
            color: "#fff",
            borderRadius: "50%",
            cursor: "pointer",
          }}
        >
          ×
        </button>

        <div className="mb-4 flex shrink-0 items-center justify-between pr-12 md:pr-8">
          <h3
            id="add-widget-modal-title"
            className="min-w-0 break-words text-lg font-semibold"
            style={{ color: theme.accent_color || "#000" }}
          >
            Add Widget
          </h3>
        </div>

        <ul
          className="min-h-0 flex-1 touch-pan-y divide-y divide-gray-200 overflow-y-auto overscroll-contain"
          style={{
            maxHeight: "560px",
          }}
        >
          {filteredWidgets.length === 0 && (
            <li className="py-3 text-gray-500">No widgets available.</li>
          )}

          {filteredWidgets.map((widget) => {
            const widgetKey = getWidgetKey(widget);

            return (
              <li
                key={widgetKey}
                className="flex min-h-[48px] cursor-pointer touch-manipulation flex-col justify-center rounded px-3 py-3 md:p-2"
                style={{ transition: "background-color 0.2s ease" }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor =
                    listItemHoverStyle.backgroundColor;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={() => selectWidget(widget)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectWidget(widget);
                  }
                }}
              >
                <div
                  className="break-words font-semibold"
                  style={{ color: theme.accent_color || "#ff6600" }}
                >
                  {widget.friendly_name || widgetKey}
                </div>
                <div className="break-words text-sm text-gray-600">
                  {widget.description || "No description available"}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default AddWidgetModal;
